import { apiEndpoints, CommandResult, IndividualMemberPermission, UserSettings } from '../amorg-common';
import { AmorgRole } from '../amorg-common';
import { AmorgUserRole } from '../amorg-common';
import { PublicAmorgUser } from '../amorg-common';
import { UserCourse } from '../amorg-common';
import { commandAuthWrapper, queryAuthWrapper } from "./serverApiHelpers";

export const createUser = async (
    id: string,
    firstname: string,
    middlename: string,
    lastname: string,
    email: string,
    password: string): Promise<CommandResult> =>

    commandAuthWrapper(apiEndpoints.users, 'POST',
        {
            id: id,
            firstname: firstname,
            middlename: middlename,
            lastname: lastname,
            email: email,
            password: password
        });

export const updateUser = async (
    id: string,
    firstname: string,
    middlename: string,
    lastname: string): Promise<CommandResult> =>

    commandAuthWrapper(apiEndpoints.users_id.replace(':id', id), 'PUT',
        {
            firstname: firstname,
            middlename: middlename,
            lastname: lastname
        });

export const updateUserSettings = async (
    id: string,
    settings: UserSettings): Promise<CommandResult> =>

    commandAuthWrapper(apiEndpoints.users_id_settings.replace(':id', id), 'PUT',
        {
            settings
        });

export const updateUserInitials = async (id: string, initials: string): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_initials.replace(':id', id), 'PUT',
        { initials });

export const getUser = async (id: string): Promise<PublicAmorgUser> =>
    queryAuthWrapper(apiEndpoints.users_id.replace(':id', id));

export const getUsers = async (includeSuperAdmins?: boolean, includeDisabled?:boolean): Promise<PublicAmorgUser[]> =>
{
    const result: PublicAmorgUser[] = await queryAuthWrapper(apiEndpoints.users);
    result.sort((a, b) => a.firstname.localeCompare(b.firstname) || a.lastname.localeCompare(b.lastname));
    
    let filtered = result.filter(u => includeDisabled ? true : u.enabled);
    if (!includeSuperAdmins)
        filtered = filtered.filter(u => u.permissionGroup.id !== 'superadmin');

    return filtered;
};

export const getUserRoles = async (userId: string): Promise<AmorgUserRole[]> =>
{
    const data: AmorgUserRole[] = await queryAuthWrapper(apiEndpoints.users_id_roles.replace(':id', userId));
    return data.map(ur => ({ ...ur, start: new Date(ur.start), end: new Date(ur.end) }));
};

export const createUserRole = async (id: string, userId: string, roleId: string, start: Date, end: Date, isCurrentRole: boolean): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_roles.replace(':id', userId), 'POST',
        {
            id, roleId, start, end, isCurrentRole
        }
    );

export const updateUserRole = async (userRoleId: string, userId: string, roleId: string, start: Date, end: Date, isCurrentRole: boolean): Promise<CommandResult> =>
    commandAuthWrapper(
        apiEndpoints.users_id_roles_roleId.replace(':id', userId).replace(':roleId', userRoleId),
        'PUT',
        {
            roleId, start, end, isCurrentRole
        }
    );

export const deleteUserRole = async (userId: string, userRoleId: string): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_roles_roleId.replace(':id', userId).replace(':roleId', userRoleId), 'DELETE');

export const getUserCourses = async (userId: string): Promise<UserCourse[]> =>
    queryAuthWrapper(apiEndpoints.users_id_usercourses.replace(':id', userId));

export const getUserCourse = async (userId: string, courseId: string): Promise<UserCourse> =>
    queryAuthWrapper(apiEndpoints.users_id_usercourses_courseId.replace(':id', userId).replace(':courseId', courseId));

export const addPlannedCourse = async (userId: string, courseId: string, id: string): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_usercourses_courseId_add.replace(':id', userId).replace(':courseId', courseId), 'POST',
        {
            id: id
        }
    );

export const getRoles = async (): Promise<AmorgRole[]> =>
    queryAuthWrapper(apiEndpoints.roles);

export const updatePermissionGroup = async (
    userId: string,
    permissionGroup: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_permissiongroup_permissionGroup.replace(':id', userId).replace(':permissionGroup', permissionGroup), 'POST');

export const assignUserIndividualPermission = async (
    userId: string,
    permission: IndividualMemberPermission,
    assigned: boolean
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_individualpermissions.replace(':id', userId), 'POST', { permission, assigned });


export const requestPasswordReset = async (
    siteId: string,
    email: string,
    isSetup:boolean
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_request_passwordreset_siteId_email.replace(':siteId', siteId).replace(':email', email) + `${isSetup ? '?setup=1' : ''}`, 'POST');

export const confirmPasswordReset = async (
    siteId: string,
    userId: string,
    requestId: string,
    newPassword: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_confirm_passwordreset_siteId_userId_requestId.replace(':siteId', siteId).replace(':userId', userId).replace(':requestId', requestId), 'POST',
        { newPassword }
    );

export const requestNewEmail = async (
    siteId: string,
    userId: string,
    newEmail: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_request_newemail_siteId_userId.replace(':siteId', siteId).replace(':userId', userId), 'PUT', { newEmail });

export const confirmNewEmail = async (
    siteId: string,
    userId: string,
    requestId: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_confirm_newemail_siteId_userId_requestId.replace(':siteId', siteId).replace(':userId', userId).replace(':requestId', requestId), 'POST');

export const requestUserCreation = async (
    siteId: string,
    userId: string,
    firstname: string,
    middlename: string,
    lastname: string,
    email: string,
    password: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_request_createuser_siteId.replace(':siteId', siteId), 'POST',
        { userId, firstname, middlename, lastname, email, password });

export const confirmUserCreation = async (
    siteId: string,
    userId: string,
    requestId: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_confirm_createuser_siteId_userId_requestId.replace(':siteId', siteId).replace(':userId', userId).replace(':requestId', requestId), 'POST');

export const changeUserPassword = async (
    userId: string,
    oldPassword: string,
    newPassword: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_changepassword.replace(':id', userId), 'POST', { oldPassword, newPassword });

export const adminUpdateEmail = async (
    userId: string,
    newEmail: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_adminupdateemail.replace(':id', userId), 'POST', { newEmail });

export const adminUpdatePassword = async (
    userId: string,
    newPassword: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_adminupdatepassword.replace(':id', userId), 'POST', { newPassword });

export const sendUserIntroEmail = async (
    id: string,
    message: string
): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_introemail, 'POST', { id, message });

export const getLastUserIntroEmailSent = async (
    userId: string
): Promise<Date | null> =>
{
    const dateString:string | null = await queryAuthWrapper(apiEndpoints.users_id_lastintroemail.replace(':id', userId));
    return dateString ? new Date(dateString) : null;
}

export const updateUserStatus = async (id: string, enabled: boolean): Promise<CommandResult> =>
    commandAuthWrapper(apiEndpoints.users_id_status.replace(':id', id), 'PUT', { enabled });